import Axios, { CancelTokenSource } from 'axios';
import { action, Action, thunk, Thunk, thunkOn, ThunkOn } from 'easy-peasy';
import ResourceQuery from 'Features/Query/ResourceQuery';
import { TFilter } from 'Models/AppModels';
import CollectModel from 'Models/Collect';
import ResourceModel from 'Models/Resource';
import { TResource } from 'Models/Resource/@types';
import { parseResource } from 'Models/Resource/resourceParsers';
// eslint-disable-next-line import/no-cycle
import { TRootStore } from 'Stores';

export interface CollectState {
  collectDetail?: TResource;
  setCollectDetail: Action<CollectState, TResource | undefined>;
  fetchCollectDetail: Thunk<CollectState, { id: string, filters?: TFilter }, null, TRootStore>;
  loadingDetails: boolean;
  setLoadingDetails: Action<CollectState, boolean>;

  collectList: TResource[];
  fetchCollectList: Thunk<CollectState, ResourceQuery, null, TRootStore>;
  setCollectList: Action<CollectState, { collectList: TResource[] }>;

  loading: boolean;
  setLoading: Action<CollectState, boolean>;

  total: number;
  setTotal: Action<CollectState, number>;

  query: ResourceQuery;
  setQuery: Action<CollectState, ResourceQuery>;
  onQueryChange: ThunkOn<CollectState, Record<string, unknown>, TRootStore>;
}

let cancelToken: CancelTokenSource | undefined;

const CollectStore: CollectState = {
  loadingDetails: false,
  collectList: [],
  total: 0,
  loading: false,
  query: new ResourceQuery({}, { page: 1 }),

  setCollectDetail: action((state, payload) => {
    state.collectDetail = payload;
  }),
  setLoadingDetails: action((state, payload) => {
    state.loadingDetails = payload;
  }),

  setCollectList: action((state, { collectList }) => {
    state.collectList = collectList;
  }),
  setTotal: action((state, payload) => {
    state.total = payload;
  }),
  setQuery: action((state, payload) => {
    state.query = payload;
  }),
  setLoading: action((state, payload) => {
    state.loading = payload;
  }),
  fetchCollectList: thunk(async (actions, query, { getStoreState }) => {
    actions.setLoading(true);
    const { community } = getStoreState().CommunityStore;
    if (!community) return;

    query.communityId = community?.id ?? '';
    query.docType = 'Collect';

    if (cancelToken) {
      cancelToken.cancel('Operation canceled due to new request.');
    }
    cancelToken = Axios.CancelToken.source();

    const res = await ResourceModel.search(
      community?.id ?? '',
      '',
      {
        Collect: {
          include: 'tags',
        },
        ...query.searchFilter,
      },
      { cancelToken: cancelToken.token }
    );

    if (res) {
      const collectList: TResource[] = [...res.results.map((res) => parseResource(res.hit))];

      actions.setTotal(res.total);
      actions.setCollectList({ collectList });
    }
    actions.setLoading(false);
  }),
  fetchCollectDetail: thunk(async (actions, { id, filters = {} }) => {
    try {
      actions.setLoadingDetails(true);
      const res = await CollectModel.getCollect(id, filters);
      actions.setCollectDetail(res);
    } catch (error) {
      console.error(error)
    } finally {
      actions.setLoadingDetails(false);
    }
  }),
  onQueryChange: thunkOn(
    (actions) => actions.setQuery,
    async (actions, { payload }) => {
      actions.fetchCollectList(payload);
    }
  ),
};

export default CollectStore;
